import { formatAddress } from '@/helpers'
import { cn } from '@/theme/utils'
import {
  UiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'

import GradientWrapper from './GradientWrapper'
import { useValidatorsList } from './hooks/useValidatorsList'

type ValidatorSelectProps = {
  value?: string
  onChange: (operatorAddress: string) => void

  disabled?: boolean
  size?: 'sm' | 'default'

  triggerClassName?: string
  contentClassName?: string
  itemClassName?: string

  wrapperClassName?: string
  wrapperOuterClassName?: string
}

function ValidatorSelect({
  value,
  onChange,
  disabled,
  size = 'default',
  triggerClassName,
  contentClassName,
  itemClassName,
  wrapperClassName,
  wrapperOuterClassName,
}: ValidatorSelectProps) {
  const { validators, isLoading, isError } = useValidatorsList()

  const activeValidators = (validators ?? [])
    .filter(
      validator =>
        !validator.jailed && validator.status === 'BOND_STATUS_BONDED',
    )
    .sort((a, b) =>
      (a.description?.moniker ?? '').localeCompare(
        b.description?.moniker ?? '',
      ),
    )

  const placeholder = (() => {
    if (isLoading) return 'Loading validators...'
    if (isError) return 'Failed to load validators'
    if (activeValidators.length === 0) return 'No active validators'
    return 'Select validator'
  })()

  return (
    <GradientWrapper
      className={cn('h-12', wrapperClassName)}
      outerClassName={wrapperOuterClassName}
    >
      <UiSelect
        value={value}
        onValueChange={onChange}
        disabled={disabled || isLoading || isError}
      >
        <UiSelectTrigger
          size={size}
          className={cn(
            'h-12 w-full data-[size=default]:h-12 data-[size=sm]:h-12',
            triggerClassName,
          )}
        >
          <UiSelectValue placeholder={placeholder} />
        </UiSelectTrigger>

        <UiSelectContent className={contentClassName}>
          {activeValidators.map(v => {
            const moniker = v.description?.moniker || 'Unnamed'
            const addr = v.operator_address

            return (
              <UiSelectItem key={addr} value={addr} className={itemClassName}>
                <span>{moniker}</span>
                <span className='text-muted-foreground'>
                  {formatAddress(addr)}
                </span>
              </UiSelectItem>
            )
          })}
        </UiSelectContent>
      </UiSelect>
    </GradientWrapper>
  )
}

export default ValidatorSelect
